/**
 * Scrubber hub: one Scrubber app, two CMS protocol packages. The user picks
 * the audit year at the top and the whole validation logic adjusts to it:
 *   2026  audits through 2026: the current package (OMB 0938-1395, expiring
 *         01/31/2027): validation, submission, review, FDR vendors, analytics.
 *   2027  the 2027 audit year and later: the CMS-10717 package (OMB approval
 *         through 10/31/2029), which CMS applies starting with the 2027 audit
 *         year per its responses to comments. Strict record layout validation
 *         with the full CMS error catalog and attestation.
 * The choice is remembered per browser and can be forced with ?year=2027.
 */
import React, { useEffect, useState } from 'react';
import ScrubberApp from './Scrubber/ScrubberApp';
import Scrubber2029App from './Scrubber2029/Scrubber2029App';

const STORE_KEY = 'scrubber_audit_year';
export const YEARS = [
  { id: '2026', label: 'Audits through 2026', hint: 'Current package, OMB 0938-1395, expires 01/31/2027. Validation, submissions, review and FDR vendors.' },
  { id: '2027', label: '2027 audit year and later', hint: 'CMS-10717 package, effective with the 2027 audit year (OMB approval through 10/31/2029). Strict record layout validation with the full CMS error catalog.' },
];
// An earlier build used the OMB expiration year as the id; map it to the audit year.
const LEGACY = { 2029: '2027' };
const norm = (v) => { const s = LEGACY[v] || String(v || ''); return YEARS.some((y) => y.id === s) ? s : null; };

function readInitialYear(initialYear) {
  if (norm(initialYear)) return norm(initialYear);
  try { const q = norm(new URLSearchParams(window.location.search).get('year')); if (q) return q; } catch { /* no window */ }
  try { const s = norm(window.localStorage.getItem(STORE_KEY)); if (s) return s; } catch { /* storage blocked */ }
  return '2026';
}

export default function ScrubberHub({ user, initialYear }) {
  const [year, setYear] = useState(() => readInitialYear(initialYear));

  useEffect(() => {
    try { window.localStorage.setItem(STORE_KEY, year); } catch { /* storage blocked */ }
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('year') !== year) {
        url.searchParams.set('year', year);
        window.history.replaceState(window.history.state, '', url.toString());
      }
    } catch { /* no window */ }
  }, [year]);

  const current = YEARS.find((y) => y.id === year) || YEARS[0];

  return (
    <div className="bg-[#f8fafc] min-h-screen">
      <div className="px-6 pt-5 -mb-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">CMS audit year</div>
            <div className="text-xs text-slate-600 mt-0.5">{current.hint}</div>
          </div>
          <div className="inline-flex rounded-full bg-slate-100 p-1" role="tablist" aria-label="Audit year">
            {YEARS.map((y) => {
              const on = y.id === year;
              return (
                <button key={y.id} type="button" role="tab" aria-selected={on} onClick={() => setYear(y.id)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${on ? 'bg-orange-500 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}>
                  {y.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {year === '2027' ? <Scrubber2029App key="2027" user={user} /> : <ScrubberApp key="2026" user={user} />}
    </div>
  );
}
